import { zodResolver } from '@hookform/resolvers/zod'
import { Save } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Container,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
} from '@mui/material'
import Typography from '@mui/material/Typography'
import { FormCheckbox } from 'components/FormItem/form-checkbox'
import { FormInput } from 'components/FormItem/form-input'
import { useMutateData } from 'hooks/use-mutate-data'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'

import { Controller, useForm } from 'react-hook-form'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { apiV1 } from 'services'
import { toast } from 'sonner'
import { validation } from 'utils'
import { z } from 'zod'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'

const signUpSchema = z
  .object({
    email: z
      .string()
      .email('É necessário preencher um email válido')
      .nonempty('É necessário preencher o email'),
    confirmarEmail: z
      .string()
      .nonempty('É necessário preencher a confirmação de e-mail'),
    nome: z.string().min(1, 'É necessário preencher o sobrenome'),
    cpf: z
      .string()
      .nonempty('É necessário preencher o CPF')
      .refine((val) => validation.isValidCPF(val), {
        message: 'CPF Inválido',
      }),
    data_nascimento: z.string().nonempty('Informe a data de nascimento'),
    telefone: z
      .string()
      .length(15, 'O número deve conter 11 dígitos')
      .nonempty('É necessário preencher o telefone'),
    sexo: z.string().refine((val) => val !== '0', {
      message: 'Por favor selecione o sexo',
    }),
    cod_brasil_cidade: z.number().refine((val) => val !== 0, {
      message: 'É necessário selecionar a cidade',
    }),
    senha: z
      .string()
      .min(8, 'A senha precisa ter no mínimo 8 caracteres')
      .nonempty('É necessário preencher a senha'),
    confirmarSenha: z
      .string()
      .nonempty('É necessário preencher a confirmação de senha'),
    confirmarTermos: z.boolean().refine((val) => val === true, {
      message: 'É necessário aceitar os termos de uso',
    }),
  })
  .superRefine((data, ctx) => {
    if (data.confirmarEmail !== data.email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Os e-mails devem ser iguais',
        path: ['confirmarEmail'],
      })
    }
    if (data.confirmarSenha !== data.senha) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'As senhas devem ser iguais',
        path: ['confirmarSenha'],
      })
    }
  })

export type SignupFields = z.infer<typeof signUpSchema>

export default function SignUpPage() {
  const [ufs, setUfs] = useState<{ uf: string }[]>([])
  const [cities, setCities] = useState<
    { cidade: string; codigo_cidade: number }[]
  >([])
  const [selectedUF, setSelectedUF] = useState<string>('SP')
  const navigate = useNavigate()

  const form = useForm<SignupFields>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      email: '',
      confirmarEmail: '',
      nome: '',
      cpf: '',
      data_nascimento: moment().format('YYYY-MM-DD'),
      telefone: '',
      sexo: '0',
      cod_brasil_cidade: 0,
      senha: '',
      confirmarSenha: '',
      confirmarTermos: false,
    },
  })

  const saveUser = useMutateData(apiV1.userService.create, {
    onSuccess: () => {
      toast.success('Usuário cadastrado com sucesso')
      navigate('/')
    },
    onError: (error) => {
      toast.error('Erro ao cadastrar usuário: ' + error.message)
    },
  })

  const verifyEmailCpf = useMutateData(apiV1.userService.existInDatabase, {
    onSuccess: (data) => {
      if (data.data.cpf) toast.error('CPF já cadastrado')
      if (data.data.email) toast.error('E-mail já cadastrado')
      if (data.data.phone) toast.error('Telefone já cadastrado')
    },
    onError: (error) => {
      toast.error('Erro ao verificar dados: ' + error.message)
    },
  })

  useEffect(() => {
    apiV1.cityService
      .getUFs()
      .then((resUF) => {
        setUfs(resUF.data)

        apiV1.cityService.getCities(selectedUF).then((res) => {
          setCities(res.data)
        })
      })
      .catch((error) => console.log(error))
  }, [selectedUF])

  const handleChangeUF = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      if (event) {
        setSelectedUF(event.target.value)

        apiV1.cityService.getCities(event.target.value).then((res) => {
          setCities(res.data)
        })
        form.setValue('cod_brasil_cidade', 0)
      }
    },
    [form],
  )

  const handleVerifyEmailCpf = useCallback(async () => {
    const data = form.getValues()
    if (!data.email || !data.cpf || !data.telefone) return
    await verifyEmailCpf.mutateAsync({
      email: data.email,
      cpf: data.cpf,
      phone: data.telefone,
    })
  }, [form, verifyEmailCpf])

  const handleSubmit = form.handleSubmit(
    useCallback(
      async (data: SignupFields) => {
        await saveUser.mutateAsync({
          email: data.email,
          nome: data.nome,
          cpf: data.cpf,
          data_nascimento: data.data_nascimento,
          telefone: data.telefone,
          sexo: data.sexo,
          cod_brasil_cidade: data.cod_brasil_cidade,
          senha: data.senha,
          confirmarSenha: data.confirmarSenha,
        })
      },
      [saveUser],
    ),
  )

  return (
    <Container maxWidth="md" component={Paper} sx={{ py: 1, my: 2 }}>
      <Typography variant="h4" textAlign="center" mt={1} mb={3}>
        Faça seu cadastro
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2} mt={2} mb={2}>
          <Grid item xs={12} sm={6}>
            <FormInput
              control={form.control}
              name="email"
              label="E-mail"
              onBlur={handleVerifyEmailCpf}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              control={form.control}
              name="confirmarEmail"
              label="Confirmar E-mail"
              onBlur={handleVerifyEmailCpf}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              control={form.control}
              name="cpf"
              label="CPF"
              mask="999.999.999-99"
              onBlur={handleVerifyEmailCpf}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              control={form.control}
              name="telefone"
              label="Telefone"
              mask="(99) 99999-9999"
              onBlur={handleVerifyEmailCpf}
            />
          </Grid>

          <Grid item xs={12}>
            <FormInput
              control={form.control}
              name="nome"
              label="Nome completo"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              control={form.control}
              name="data_nascimento"
              label="Data de nascimento"
              type="date"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!form.formState.errors.sexo}>
              <InputLabel id="sexo-select-label">Sexo</InputLabel>
              <Controller
                name="sexo"
                control={form.control}
                rules={{ required: 'Sexo é obrigatório' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="sexo-select-label"
                    id="sexo-select"
                    label="Sexo"
                    defaultValue="0"
                  >
                    <MenuItem value={'Masculino'}>Masculino</MenuItem>
                    <MenuItem value={'Feminino'}>Feminino</MenuItem>
                    <MenuItem value={'Prefiro não informar'}>
                      Prefiro não informar
                    </MenuItem>
                    <MenuItem value={'0'}>Selecione</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel id="uf-select-label">UF</InputLabel>
              <Select
                labelId="uf-select-label"
                id="uf-select"
                label="UF"
                value={selectedUF}
                onChange={handleChangeUF}
              >
                {ufs?.map((city) => (
                  <MenuItem key={'uf_' + city.uf} value={city.uf}>
                    {city.uf}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={10}>
            {/* Corrigido: usar Controller + Autocomplete para evitar erro de focus */}
            <Controller
              name="cod_brasil_cidade"
              control={form.control}
              rules={{ required: 'Cidade é obrigatória' }}
              render={({ field }) => (
                <Autocomplete
                  disablePortal
                  id="cod_brasil_cidade-select"
                  options={cities}
                  getOptionLabel={(option) => option.cidade}
                  isOptionEqualToValue={(option, value) =>
                    option.codigo_cidade === value.codigo_cidade
                  }
                  value={
                    cities.find((city) => city.codigo_cidade === field.value) ||
                    null
                  }
                  onChange={(_, value) =>
                    field.onChange(value ? value.codigo_cidade : 0)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={field.ref}
                      label="Cidade"
                      error={!!form.formState.errors.cod_brasil_cidade}
                      helperText={
                        form.formState.errors.cod_brasil_cidade?.message
                      }
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              control={form.control}
              name="senha"
              label="Senha"
              type="password"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              control={form.control}
              name="confirmarSenha"
              label="Confirmar senha"
              type="password"
            />
          </Grid>

          <Grid item xs={12}>
            <FormCheckbox
              control={form.control}
              name="confirmarTermos"
              label={
                <Typography>
                  Concordo com os{' '}
                  <Link
                    href={'https://zedoingresso.com.br/termos-de-uso'}
                    target="_blank"
                  >
                    termos de uso
                  </Link>{' '}
                  e com a{' '}
                  <Link
                    href={'https://zedoingresso.com.br/politica-de-privacidade'}
                    target="_blank"
                  >
                    política de privacidade
                  </Link>
                  .
                </Typography>
              }
            />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'end',
              py: 2,
            }}
          >
            <LoadingButton
              loading={saveUser.isPending}
              loadingPosition="start"
              variant="contained"
              type="submit"
              startIcon={<Save />}
            >
              Salvar
            </LoadingButton>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{ display: 'flex', justifyContent: 'flex-end', py: 2 }}
          >
            <Typography variant="body1">
              Já tem cadastro?{' '}
              <Link component={RouterLink} to={'/login'}>
                entre aqui
              </Link>
            </Typography>
          </Grid>
        </Grid>
      </form>
    </Container>
  )
}
